import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "react-query";
import { useRecoilValue } from "recoil";
import { CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import DownloadIcon from "@mui/icons-material/GetApp";
import { useSnackbar } from "notistack";

import { Row } from "../Layout/utils";
import {
  exportAlerts,
  exportPriceReport,
  exportOptiReport,
} from "../services/Alert";
import { granularityAtom, orderingAtom } from "./AlertGranularityTabs";
import { filtersAtom } from "./Filter/PerimeterFilter";
import { kpiSelectedAtom } from "./atom";
import { ActionTooltip } from "../components/Tooltips";

function ExportAlertMenuItem({ onClick }: { onClick: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const filters = useRecoilValue(filtersAtom);
  const granularityValue = useRecoilValue(granularityAtom);
  const kpiSelected = useRecoilValue(kpiSelectedAtom);
  const ordering = useRecoilValue(orderingAtom);
  const [isDisabled, setIsDisabled] = useState(false);

  const mutation = useMutation(exportAlerts);

  const exportFile = () => {
    setIsDisabled(true);
    mutation.mutate(
      {
        filters: filters,
        kpiIds: kpiSelected.map((obj) => obj.id),
        granularity: granularityValue,
        ordering: ordering,
        // Same translated headers as the alerts table, so the file follows the
        // user's language and industry wording.
        columnLabels: {
          property: intl.formatMessage({ id: "COLUMN_NAME_HEBERGEMENT" }),
          uh_type_nandc: intl.formatMessage({ id: "COLUMN_NAME_UH_TYPE" }),
          uh_cat: intl.formatMessage({ id: "COLUMN_NAME_UH_CAT" }),
          date: intl.formatMessage({ id: "DATE" }),
          event: intl.formatMessage({ id: "COLUMN_NAME_EVENT" }),
        },
      },
      {
        onSettled: (data, error) => {
          // We disable the button for 5 secs to avoid spam
          setTimeout(() => {
            setIsDisabled(false);
          }, 5000);
        },
        onSuccess: () => {
          enqueueSnackbar(intl.formatMessage({ id: "EXPORT_FILE_SUCCESS" }), {
            variant: "success",
          });
        },
        onError: () => {
          enqueueSnackbar(intl.formatMessage({ id: "EXPORT_FILE_FAILED" }), {
            variant: "error",
          });
        },
      }
    );
  };

  const handleClick = () => {
    exportFile();
    onClick();
  };

  return (
    <>
      <MenuItem onClick={handleClick} disabled={isDisabled}>
        <FormattedMessage id="EXPORT_ALERTS" />
      </MenuItem>
    </>
  );
}

function ExportOptiReportMenuItem({ onClick }: { onClick: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [isDisabled, setIsDisabled] = useState(false);

  const { mutate } = useMutation(exportOptiReport);

  const exportFile = () => {
    setIsDisabled(true);
    // only void 0 or undefined are accepted as empty variables
    mutate(undefined, {
      onSettled: (data, error) => {
        onClick();
        setIsDisabled(false);
      },
      onSuccess: () => {
        enqueueSnackbar(intl.formatMessage({ id: "EXPORT_FILE_DOWNLOAD" }), {
          variant: "success",
        });
      },
      onError: () => {
        enqueueSnackbar(intl.formatMessage({ id: "EXPORT_FILE_FAILED" }), {
          variant: "error",
        });
      },
    });
  };

  const handleClick = () => {
    exportFile();
  };

  return (
    <>
      <MenuItem onClick={handleClick} disabled={isDisabled}>
        <Row
          sx={{
            alignItems: "center",
            gap: 1,
          }}
        >
          {isDisabled && <CircularProgress size="1rem" />}
          <FormattedMessage id="EXPORT_OPTIMIZATION_REPORT" />
        </Row>
      </MenuItem>
    </>
  );
}

function ExportPriceMenuItem({
  running,
  onRunningChange,
}: {
  running: boolean;
  onRunningChange: (running: boolean) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const { mutate } = useMutation(exportPriceReport);

  const exportFile = () => {
    // The report is generated asynchronously and can take a while for big
    // clients. Poll in the background and download the file automatically once
    // it is ready. Notify (with an ETA when we have history) as soon as the
    // task is queued.
    onRunningChange(true);
    const notifyStarted = (estimatedSeconds: number | null) => {
      // No history yet on the first run -> show "N/A" rather than a fake ETA.
      const minutes =
        estimatedSeconds && estimatedSeconds > 0
          ? `~${Math.max(1, Math.round(estimatedSeconds / 60))} min`
          : "N/A";
      enqueueSnackbar(
        intl.formatMessage(
          { id: "EXPORT_PRICES_REPORT_GENERATING" },
          { minutes }
        ),
        { variant: "info" }
      );
    };

    mutate(notifyStarted, {
      onSuccess: () => {
        enqueueSnackbar(intl.formatMessage({ id: "EXPORT_FILE_DOWNLOAD" }), {
          variant: "success",
        });
      },
      onError: () => {
        enqueueSnackbar(intl.formatMessage({ id: "EXPORT_FILE_FAILED" }), {
          variant: "error",
        });
      },
      onSettled: () => {
        onRunningChange(false);
      },
    });
  };

  // Keep the dropdown open on click so the spinner is visible on the item;
  // the generation runs in the background.
  return (
    <MenuItem onClick={exportFile} disabled={running}>
      <Row
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          width: "100%",
        }}
      >
        <FormattedMessage id="EXPORT_PRICES_REPORT" />
        {running && <CircularProgress size="1rem" />}
      </Row>
    </MenuItem>
  );
}

export const ExportAlertMenu = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  // Local state (like the other async-export flows, e.g. BookingTableToolbar):
  // survives menu open/close on this page, cleared on navigation / full reload.
  const [pricesRunning, setPricesRunning] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <ActionTooltip title="EXPORT_INFO" rawChildren>
        <IconButton
          aria-controls="export-menu"
          aria-haspopup="true"
          sx={{ color: "text.accentInverted" }}
          size="small"
          onClick={handleClick}
        >
          <DownloadIcon fontSize="inherit" />
        </IconButton>
      </ActionTooltip>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <ExportAlertMenuItem onClick={handleClose} />
        <ExportOptiReportMenuItem onClick={handleClose} />
        <ExportPriceMenuItem
          running={pricesRunning}
          onRunningChange={setPricesRunning}
        />
      </Menu>
    </>
  );
};
